import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  BillingCharge,
  BillingSetPaymentMethodRequest,
  BillingSetPaymentMethodResponse,
  BillingCreateBankTransferChargeRequest,
  BillingCreateBankTransferChargeResponse,
  BillingUpdateBankTransferChargeRequest,
  BillingUpdateBankTransferChargeResponse,
} from '@kawai/shared';
import { fetchBankTransferCharges } from '../repositories/billingCharges.js';
import {
  callBillingSetPaymentMethod,
  callBillingCreateBankTransferCharge,
  callBillingUpdateBankTransferCharge,
} from '../api/billingBankTransfer.js';

// TP-27: 振込請求管理ページの React Query hooks。
// cache key は横断一覧が ['bank_transfer_charges']（テナント別 ['billing_charges', tenantId] とは別系統）。
// mutation 成功時は横断一覧 + 当該テナントの明細（BillingDetailPage）を invalidate する。

/** TP-27: 銀行振込の請求横断一覧（saas-admin / read 専用）。 */
export function useBankTransferCharges(): UseQueryResult<
  BillingCharge[],
  Error
> {
  return useQuery({
    queryKey: ['bank_transfer_charges'],
    queryFn: () => fetchBankTransferCharges(),
  });
}

/** TP-27: `billingSetPaymentMethod` mutation。成功時は tenants 一覧を invalidate。 */
export function useBillingSetPaymentMethod(): UseMutationResult<
  BillingSetPaymentMethodResponse,
  Error,
  BillingSetPaymentMethodRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BillingSetPaymentMethodRequest) =>
      callBillingSetPaymentMethod(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
}

/** TP-27: `billingCreateBankTransferCharge` mutation。成功時は請求一覧系を invalidate。 */
export function useCreateBankTransferCharge(): UseMutationResult<
  BillingCreateBankTransferChargeResponse,
  Error,
  BillingCreateBankTransferChargeRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BillingCreateBankTransferChargeRequest) =>
      callBillingCreateBankTransferCharge(input),
    onSuccess: async (_res, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['bank_transfer_charges'] }),
        queryClient.invalidateQueries({
          queryKey: ['billing_charges', variables.tenantId],
        }),
      ]);
    },
  });
}

/** TP-27: `billingUpdateBankTransferCharge` mutation（消し込み / 取消）。 */
export function useUpdateBankTransferCharge(): UseMutationResult<
  BillingUpdateBankTransferChargeResponse,
  Error,
  BillingUpdateBankTransferChargeRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BillingUpdateBankTransferChargeRequest) =>
      callBillingUpdateBankTransferCharge(input),
    onSuccess: async (_res, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['bank_transfer_charges'] }),
        queryClient.invalidateQueries({
          queryKey: ['billing_charges', variables.tenantId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['billing_charge', variables.tenantId, variables.chargeId],
        }),
      ]);
    },
  });
}
